import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { FileText, Search, Filter, Eye } from "lucide-react";
import { format } from "date-fns";

interface IntakeForm {
  id: string;
  client_id: string;
  appointment_id: string | null;
  status: string;
  form_data: any;
  sent_at: string | null;
  completed_at: string | null;
  created_at: string;
}

export default function IntakeForms() {
  const [forms, setForms] = useState<IntakeForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedForm, setSelectedForm] = useState<IntakeForm | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchForms();

    const channel = supabase
      .channel("intake-forms-changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "intake_forms" },
        () => {
          fetchForms();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchForms = async () => {
    try {
      const { data, error } = await supabase
        .from("intake_forms")
        .select(`
          *,
          appointments:appointment_id (
            preferred_date,
            preferred_time,
            service_type
          )
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setForms(data || []);
    } catch (error) {
      console.error("Error fetching intake forms:", error);
      toast({
        title: "Error",
        description: "Failed to load intake forms",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from("intake_forms")
        .update({ status: newStatus })
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Form status updated",
      });
      fetchForms();
    } catch (error) {
      console.error("Error updating status:", error);
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive",
      });
    }
  };

  const filteredForms = forms.filter((form) => {
    const matchesStatus = statusFilter === "all" || form.status === statusFilter;
    return matchesStatus;
  });

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      pending: "secondary",
      sent: "default",
      completed: "outline",
    };
    return <Badge variant={variants[status] || "outline"}>{status}</Badge>;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <FileText className="h-8 w-8" />
            Smart Intake Forms
          </h1>
          <p className="text-muted-foreground mt-2">
            View and manage client intake forms
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Intake Forms</CardTitle>
            <CardDescription>
              Review client-submitted intake forms
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[200px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="text-center py-8">Loading forms...</div>
            ) : filteredForms.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No intake forms found
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client ID</TableHead>
                      <TableHead>Appointment</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Sent</TableHead>
                      <TableHead>Completed</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredForms.map((form) => (
                      <TableRow key={form.id}>
                        <TableCell className="font-mono text-sm">
                          {form.client_id.slice(0, 8)}...
                        </TableCell>
                        <TableCell>
                          {form.appointment_id && (form as any).appointments ? (
                            <div className="text-sm">
                              <div className="font-medium">
                                {(form as any).appointments.service_type}
                              </div>
                              {(form as any).appointments.preferred_date && (
                                <div className="text-muted-foreground">
                                  {format(new Date((form as any).appointments.preferred_date), "MMM d, yyyy")}
                                  {(form as any).appointments.preferred_time && 
                                    ` at ${(form as any).appointments.preferred_time}`
                                  }
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">Not linked</span>
                          )}
                        </TableCell>
                        <TableCell>{getStatusBadge(form.status)}</TableCell>
                        <TableCell>
                          {format(new Date(form.created_at), "MMM d, yyyy")}
                        </TableCell>
                        <TableCell>
                          {form.sent_at ? (
                            format(new Date(form.sent_at), "MMM d, yyyy")
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {form.completed_at ? (
                            format(new Date(form.completed_at), "MMM d, yyyy")
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setSelectedForm(form)}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View
                            </Button>
                            {form.status === "pending" && (
                              <Button
                                size="sm"
                                onClick={() => updateStatus(form.id, "sent")}
                              >
                                Mark Sent
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={!!selectedForm} onOpenChange={() => setSelectedForm(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Intake Form Data</DialogTitle>
              <DialogDescription>
                Review the submitted form information
              </DialogDescription>
            </DialogHeader>
            {selectedForm && (
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">Status</h3>
                  {getStatusBadge(selectedForm.status)}
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Form Data</h3>
                  <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm">
                    {JSON.stringify(selectedForm.form_data, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
